import bridge from '@vkontakte/vk-bridge';

/**
 * Обёртки над мостом ВК.
 *
 * **Запасных путей тут нет, и заводить их не надо.** Приложение живёт внутри
 * клиента ВК и нигде больше. Браузерные заменители (`navigator.clipboard`,
 * `window.open`) отсюда снесены как мёртвый код: снаружи ВК до них всё равно
 * не доходило — отвечать на запрос моста некому, `send` не разрешается ни
 * успехом, ни ошибкой, и строка под ним не выполняется никогда. Проверка
 * окружения ушла по той же причине: проверять нечего, мы всегда внутри.
 *
 * Следствие, которое надо помнить: в обычном браузере эти вызовы ВИСЯТ, и
 * экран, который их ждёт, остаётся в состоянии загрузки. Смотреть такие места
 * можно только в клиенте ВК.
 */

/** Копирование в буфер обмена. Отказ клиента уходит наверх — его показывает экран. */
export async function copyText(text: string): Promise<void> {
  await bridge.send('VKWebAppCopyText', { text });
}

/**
 * Полноэкранный просмотр фотографий силами клиента ВК: зум, свайп между
 * снимками и сохранение достаются бесплатно, своей галереи писать не нужно.
 * Галерея принимает ВЕСЬ набор соседних фото — она листается свайпом, и с
 * одной картинкой свайп упирается в пустоту.
 * Адреса обязательно абсолютные — см. absoluteUrl в shared/lib/media.ts.
 */
export async function showImages(images: string[], startIndex = 0): Promise<void> {
  const list = images.filter((url) => Boolean(url));
  if (list.length === 0) return;

  const start = Math.min(Math.max(startIndex, 0), list.length - 1);

  try {
    await bridge.send('VKWebAppShowImages', { images: list, start_index: start });
  } catch {
    // Зовут без await (`void showImages(...)`), и отказ клиента значит
    // «галерея не открылась», а не ошибку экрана. Но проглотить его надо
    // здесь: иначе в консоль улетит необработанный отказ промиса.
  }
}

/**
 * Имя страницы ВК: под ним человек попадёт в список участников.
 *
 * Пустое имя не беда — администратор впишет его сам, а найдут человека по
 * номеру страницы, он приходит из подписи параметров запуска. Поэтому отказ
 * в доступе к профилю не мешает записаться.
 */
export async function getDisplayName(): Promise<string | null> {
  try {
    const info = await bridge.send('VKWebAppGetUserInfo');
    const name = [info.first_name, info.last_name].filter(Boolean).join(' ').trim();
    return name === '' ? null : name;
  } catch {
    return null;
  }
}
